import { useEffect, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ProductAdmin } from "@/components/ProductAdmin";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("ess_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [orders, setOrders] = useState(null);
  const [tab, setTab] = useState("inquiries");

  const headers = { Authorization: `Bearer ${token}` };

  const load = async (t = token) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/inquiries", { headers: { Authorization: `Bearer ${t}` } });
      setItems(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("ess_admin_token");
        setToken("");
      } else {
        setError(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const { data } = await api.get("/admin/orders", { headers });
      setOrders(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token && tab === "orders" && !orders) loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem("ess_admin_token", data.token);
      setToken(data.token);
      load(data.token);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const toggleStatus = async (item) => {
    const next = item.status === "new" ? "reviewed" : "new";
    await api.patch(`/admin/inquiries/${item.id}`, { status: next }, { headers });
    setItems((list) => list.map((x) => (x.id === item.id ? { ...x, status: next } : x)));
  };

  const toggleOrder = async (o) => {
    const next = o.status === "new" ? "fulfilled" : "new";
    await api.patch(`/admin/orders/${o.id}`, { status: next }, { headers });
    setOrders((list) => list.map((x) => (x.id === o.id ? { ...x, status: next } : x)));
  };

  const logout = () => {
    localStorage.removeItem("ess_admin_token");
    setToken("");
    setItems(null);
    setOrders(null);
  };

  return (
    <div className="min-h-screen bg-paper px-6 py-16" data-testid="admin-page">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-fog">ESSANDTIALS</p>
            <h1 className="mt-2 font-serif text-3xl uppercase text-ink">Dashboard</h1>
          </div>
          {token && (
            <div className="flex gap-3">
              <button
                onClick={() => (tab === "inquiries" ? load() : tab === "orders" ? loadOrders() : window.location.reload())}
                data-testid="admin-refresh-button"
                className="btn btn-outline !px-5 !py-2.5"
              >
                <RefreshCw size={13} strokeWidth={1.5} /> Refresh
              </button>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="btn btn-dark !px-5 !py-2.5"
              >
                <LogOut size={13} strokeWidth={1.5} /> Log out
              </button>
            </div>
          )}
        </div>

        {!token ? (
          <form onSubmit={login} className="mx-auto mt-20 max-w-sm" data-testid="admin-login-form">
            <label className="lbl" htmlFor="admin-password">Admin password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field"
              data-testid="admin-password-input"
              required
            />
            {error && (
              <p className="mt-3 text-sm text-red-700" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button type="submit" className="btn btn-dark mt-5 w-full" data-testid="admin-login-button">
              Enter
            </button>
          </form>
        ) : (
          <>
            <div className="mt-8 flex gap-2" data-testid="admin-tabs">
              {[
                ["inquiries", "Booking Inquiries"],
                ["orders", "Shop Reservations"],
                ["products", "Shop Products"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  data-testid={`admin-tab-${key}`}
                  className={`rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    tab === key ? "bg-ink text-paper" : "border border-beige text-clay hover:border-sanddeep hover:text-ink"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "inquiries" && (
              <div className="mt-8" data-testid="admin-inquiries-list">
                {loading && !items && <p className="text-sm text-fog">Loading…</p>}
                {items && items.length === 0 && (
                  <p className="rounded-2xl border border-beige/60 bg-white/70 p-8 text-sm text-clay">
                    No inquiries yet. Bookings submitted from the Book page will appear here.
                  </p>
                )}
                <div className="space-y-4">
                  {items &&
                    items.map((it) => (
                      <div
                        key={it.id}
                        className="rounded-2xl border border-beige/60 bg-white/80 p-6"
                        data-testid="admin-inquiry-card"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-serif text-xl text-ink">
                              {it.full_name}
                              {it.business_name ? ` · ${it.business_name}` : ""}
                            </p>
                            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-fog">
                              {it.rental_type} · {new Date(it.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${
                                it.status === "new" ? "bg-blush text-ink" : "bg-cream text-fog"
                              }`}
                              data-testid="admin-inquiry-status"
                            >
                              {it.status}
                            </span>
                            <button
                              onClick={() => toggleStatus(it)}
                              data-testid="admin-inquiry-toggle"
                              className="rounded-full border border-beige px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-clay transition-colors hover:border-sanddeep hover:text-ink"
                            >
                              Mark {it.status === "new" ? "reviewed" : "new"}
                            </button>
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-1 text-sm text-clay sm:grid-cols-2">
                          <p><span className="text-fog">Email:</span> {it.email}</p>
                          <p><span className="text-fog">Phone:</span> {it.phone}</p>
                          {it.instagram && <p><span className="text-fog">Instagram:</span> {it.instagram}</p>}
                          {it.website && <p><span className="text-fog">Website:</span> {it.website}</p>}
                          {it.business_type && <p><span className="text-fog">Business type:</span> {it.business_type}</p>}
                          {it.products_services && <p><span className="text-fog">Products/Services:</span> {it.products_services}</p>}
                          {it.requested_dates && <p><span className="text-fog">Dates:</span> {it.requested_dates}</p>}
                          {it.rental_length && <p><span className="text-fog">Length:</span> {it.rental_length}</p>}
                          {it.expected_customers && <p><span className="text-fog">Expected customers:</span> {it.expected_customers}</p>}
                          {it.special_requirements && <p><span className="text-fog">Special requirements:</span> {it.special_requirements}</p>}
                          {it.additional_services && <p><span className="text-fog">Additional services:</span> {it.additional_services}</p>}
                        </div>
                      </div>
                    ))}
                </div>
                {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
              </div>
            )}

            {tab === "orders" && (
              <div className="mt-8 space-y-4" data-testid="admin-orders-list">
                {!orders && <p className="text-sm text-fog">Loading…</p>}
                {orders && orders.length === 0 && (
                  <p className="rounded-2xl border border-beige/60 bg-white/70 p-8 text-sm text-clay">
                    No reservations yet. Shop orders placed from the Shop page will appear here.
                  </p>
                )}
                {orders &&
                  orders.map((o) => (
                    <div key={o.id} className="rounded-2xl border border-beige/60 bg-white/80 p-6" data-testid="admin-order-row">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-serif text-xl text-ink">{o.name}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-fog">
                            {new Date(o.created_at).toLocaleString()} · ${o.total.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${
                              o.status === "new" ? "bg-blush text-ink" : "bg-cream text-fog"
                            }`}
                            data-testid="admin-order-status"
                          >
                            {o.status}
                          </span>
                          <button
                            onClick={() => toggleOrder(o)}
                            data-testid="admin-order-toggle"
                            className="rounded-full border border-beige px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-clay transition-colors hover:border-sanddeep hover:text-ink"
                          >
                            Mark {o.status === "new" ? "fulfilled" : "new"}
                          </button>
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-clay">
                        <span className="text-fog">Contact:</span> {o.email} · {o.phone}
                      </p>
                      <ul className="mt-2 space-y-0.5 text-sm text-clay" data-testid="admin-order-items">
                        {o.items.map((i) => (
                          <li key={i.id}>
                            {i.quantity} × {i.name} <span className="text-fog">({i.brand})</span> — ${(i.price * i.quantity).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            )}

            {tab === "products" && <div className="mt-8"><ProductAdmin token={token} /></div>}
          </>
        )}
      </div>
    </div>
  );
}
